// askSmart-ui/src/pages/TOSP/components/UnitModal.tsx
import React, { useEffect, useMemo, useState } from "react";
import { formatCurrency } from "../format";
import { useTospData } from "../TospDataContext";
import {
  bmvaRate,
  Building,
  compareTaxRolls,
  currentAllocation,
  emptyTaxRollIdentity,
  TaxRollIdentity,
  TaxRollPart,
  TaxRollRecord,
  TaxYear,
  Unit,
} from "../types";

/** One assignable share of a roll — what the picker lists and the form holds.
 *  A whole roll has exactly one; a split roll has one per part. */
interface RollPartRef {
  roll: TaxRollRecord;
  part: TaxRollPart;
}

/** Stable across a save, since it's built from two record ids. */
const refKey = (roll: TaxRollRecord, part: TaxRollPart) => `${roll.id}:${part.id}`;

/** "000-020-16701-0000 (A)" — the roll number, plus its part when split. */
const refLabel = (ref: RollPartRef) =>
  ref.part.label ? `${ref.roll.rollNumber} (${ref.part.label})` : ref.roll.rollNumber;
import { parseNumeric, requiredNumeric, requiredText, sameText } from "../validation";

export interface UnitFormValues {
  buildingId: string;
  suiteNumber: string;
  spaceType: string;
  occupancySqFt: number;
  bmvaSqFt: number;
  /** Not edited here — the rate comes from Configuration. Written through so
   *  the unit keeps a usable fallback if the configured rate is ever cleared. */
  bmvaRatePerSqFt: number;
  /** Legacy, never edited here — kept so the first import can still match on
   *  it. See Unit.taxRoll. */
  taxRoll: TaxRollIdentity;
  /** Carried through untouched — the amounts are edited on the Tax Rolls tab. */
  taxYears: TaxYear[];
  /** Roll parts this unit should hold once saved, keyed "<rollId>:<partId>".
   *  The register is the source of truth, so the parent writes the difference
   *  against it. */
  taxRollPartKeys: string[];
}

interface UnitModalProps {
  isOpen: boolean;
  /** The unit being edited, or null when adding a new one. */
  unit: Unit | null;
  /** Source of the Building dropdown — always the live Buildings list. */
  buildings: Building[];
  /** Every unit, so the suite-uniqueness check can see its siblings. */
  units: Unit[];
  onClose: () => void;
  /** Awaited, so the button can hold until the writes are done. */
  onSave: (values: UnitFormValues) => void | Promise<void>;
}

const numberOrZero = (raw: string): number => parseNumeric(raw) ?? 0;

// Add / edit form for a single Unit. One modal serves both, so the fields,
// calculations and validation can never drift between the two paths.
export default function UnitModal({
  isOpen,
  unit,
  buildings,
  units,
  onClose,
  onSave,
}: UnitModalProps) {
  // Space Types are admin-maintained under Configuration, never hardcoded.
  const { configuration, taxRolls } = useTospData();
  const [buildingId, setBuildingId] = useState("");
  const [suiteNumber, setSuiteNumber] = useState("");
  const [spaceType, setSpaceType] = useState("");
  const [occupancySqFt, setOccupancySqFt] = useState("");
  const [bmvaSqFt, setBmvaSqFt] = useState("");
  // Roll ids attached to this unit. Staged here and written on Save, so
  // Cancel discards them the way it discards every other field — at the cost
  // of a roll staying in the pool until the form is saved.
  const [taxRollPartKeys, setTaxRollPartKeys] = useState<string[]>([]);
  const [picking, setPicking] = useState(false);
  // Ticked in the picker but not yet added — a unit like C1 takes ten rolls,
  // so they're gathered up and attached in one go rather than one at a time.
  const [checked, setChecked] = useState<string[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  // Saving a unit also rewrites every roll whose parts changed hands, so a
  // ten-roll unit is several writes — long enough that a still button reads
  // as a dead one.
  const [busy, setBusy] = useState(false);

  // Reload the form whenever it opens, so a cancelled edit leaves nothing
  // behind for the next Add.
  useEffect(() => {
    if (!isOpen) return;
    setBuildingId(unit?.buildingId || "");
    setSuiteNumber(unit?.suiteNumber || "");
    setSpaceType(unit?.spaceType || "");
    setOccupancySqFt(unit ? String(unit.occupancySqFt) : "");
    setBmvaSqFt(unit ? String(unit.bmvaSqFt) : "");
    setTaxRollPartKeys(
      unit
        ? taxRolls.flatMap((roll) =>
            currentAllocation(roll)
              .parts.filter((part) => part.assignedTo?.id === unit.id)
              .map((part) => refKey(roll, part)),
          )
        : [],
    );
    setPicking(false);
    setChecked([]);
    setErrors({});
    setBusy(false);
    // taxRolls is deliberately absent: re-reading the register mid-edit would
    // discard picks that haven't been saved yet.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, unit]);

  /** Every assignable share in the register, in bill order. */
  const allRefs = useMemo<RollPartRef[]>(
    () =>
      [...taxRolls]
        .sort(compareTaxRolls)
        .flatMap((roll) =>
          currentAllocation(roll).parts.map((part) => ({ roll, part })),
        ),
    [taxRolls],
  );

  const attached = useMemo(
    () => allRefs.filter((ref) => taxRollPartKeys.includes(refKey(ref.roll, ref.part))),
    [allRefs, taxRollPartKeys],
  );

  /** The pool, less anything already picked in this form. A part assigned to
   *  another unit never appears — that's what stops one being claimed twice. */
  const available = useMemo(
    () =>
      allRefs.filter(
        (ref) =>
          !ref.part.assignedTo && !taxRollPartKeys.includes(refKey(ref.roll, ref.part)),
      ),
    [allRefs, taxRollPartKeys],
  );

  if (!isOpen) return null;

  const closePicker = () => {
    setPicking(false);
    setChecked([]);
  };

  const addChecked = () => {
    setTaxRollPartKeys((prev) => [...prev, ...checked]);
    closePicker();
  };

  // BMVA's rate, the same for every unit — set on the Configuration page, not
  // here. The unit's own stored rate stands in only until it's entered there,
  // and an exempt Space Type pays nothing.
  //
  // Reads the Space Type being edited rather than the saved one, so switching
  // a unit to Storage shows the fee falling to zero before it's saved.
  const rate = bmvaRate(configuration, {
    id: unit?.id ?? "",
    spaceType,
    bmvaSqFt: numberOrZero(bmvaSqFt),
    bmvaRatePerSqFt: unit?.bmvaRatePerSqFt ?? 0,
  });
  const byName = !!unit && configuration.bmvaExemptUnitIds.includes(unit.id);
  const exempt = rate === 0 && configuration.bmvaRatePerSqFt !== null;

  // Recomputed on every keystroke, so the fees track BMVA SqFt live.
  const annualFee = numberOrZero(bmvaSqFt) * rate;
  const monthlyFee = annualFee / 12;

  const validate = (): { values?: UnitFormValues; errors: Record<string, string> } => {
    const next: Record<string, string> = {};

    if (!buildingId) next.buildingId = "Building is required.";
    if (!spaceType) next.spaceType = "Space Type is required.";

    const suiteError = requiredText(suiteNumber, "Suite #");
    if (suiteError) {
      next.suiteNumber = suiteError;
    } else if (
      units.some(
        (u) =>
          u.id !== unit?.id &&
          u.buildingId === buildingId &&
          sameText(u.suiteNumber, suiteNumber),
      )
    ) {
      // Suite numbers only need to be unique inside their own building.
      next.suiteNumber = "This Suite # already exists in the selected building.";
    }

    const occupancyError = requiredNumeric(occupancySqFt, "Occupancy SqFt");
    if (occupancyError) next.occupancySqFt = occupancyError;

    const bmvaError = requiredNumeric(bmvaSqFt, "BMVA SqFt");
    if (bmvaError) next.bmvaSqFt = bmvaError;

    // No check on the rate: it isn't entered here, and a unit shouldn't be
    // unsaveable because BMVA Fees hasn't been filled in on another screen.

    // Rolls are NOT required. They arrive by importing the municipality's bill
    // on the Rolls tab, which may not have happened yet — blocking a unit on a
    // roll that doesn't exist in the register would make units unaddable.

    if (Object.keys(next).length > 0) return { errors: next };
    return {
      errors: next,
      values: {
        buildingId,
        suiteNumber: suiteNumber.trim(),
        spaceType,
        occupancySqFt: numberOrZero(occupancySqFt),
        bmvaSqFt: numberOrZero(bmvaSqFt),
        bmvaRatePerSqFt: rate,
        // Never edited here any more; passed straight back so the import can
        // still match on whatever was typed before.
        taxRoll: unit?.taxRoll || emptyTaxRollIdentity(),
        // Untouched by this form — the yearly amounts belong to Tax Rolls.
        taxYears: unit?.taxYears || [],
        taxRollPartKeys,
      },
    };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (busy) return;
    const { values, errors: found } = validate();
    setErrors(found);
    if (!values) return;

    setBusy(true);
    try {
      await onSave(values);
    } finally {
      // On success the parent closes this and the reset above clears it; on
      // failure the button has to come back so the save can be retried.
      setBusy(false);
    }
  };

  return (
    <div className="smrtdocs-modal-overlay">
      <div className="smrtdocs-modal tosp-modal-wide">
        <div className="smrtdocs-modal-header">
          <h2 style={{ margin: 0, fontSize: "1rem" }}>{unit ? "Edit Unit" : "Add Unit"}</h2>
        </div>
        {/* autoComplete="off" on the form and every field: these are property
            records, not the user's own details, so the browser's saved-value
            dropdown is noise. */}
        <form className="smrtdocs-modal-body" onSubmit={handleSubmit} noValidate autoComplete="off">
          <div className="tosp-form-row">
            <div className="tosp-field">
              <label className="tosp-label" htmlFor="tosp-unit-building">
                Building <span className="tosp-required">*</span>
              </label>
              <select
                id="tosp-unit-building"
                className="manage-input"
                value={buildingId}
                onChange={(e) => setBuildingId(e.target.value)}
              >
                <option value="">— Select a building —</option>
                {buildings.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </select>
              {buildings.length === 0 && (
                <p className="tosp-field-hint">Add a building first on the Buildings tab.</p>
              )}
              {errors.buildingId && <p className="tosp-field-error">{errors.buildingId}</p>}
            </div>

            <div className="tosp-field">
              <label className="tosp-label" htmlFor="tosp-unit-suite">
                Suite # <span className="tosp-required">*</span>
              </label>
              <input
                id="tosp-unit-suite"
                className="manage-input"
                autoComplete="off"
                value={suiteNumber}
                onChange={(e) => setSuiteNumber(e.target.value)}
              />
              {errors.suiteNumber && <p className="tosp-field-error">{errors.suiteNumber}</p>}
            </div>
          </div>

          <div className="tosp-form-row">
            <div className="tosp-field">
              <label className="tosp-label" htmlFor="tosp-unit-space-type">
                Space Type <span className="tosp-required">*</span>
              </label>
              <select
                id="tosp-unit-space-type"
                className="manage-input"
                value={spaceType}
                onChange={(e) => setSpaceType(e.target.value)}
              >
                <option value="">— Select a space type —</option>
                {configuration.spaceTypes.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
              {errors.spaceType && <p className="tosp-field-error">{errors.spaceType}</p>}
            </div>

            <div className="tosp-field">
              <label className="tosp-label" htmlFor="tosp-unit-occupancy">
                Occupancy SqFt <span className="tosp-required">*</span>
              </label>
              <input
                id="tosp-unit-occupancy"
                className="manage-input"
                autoComplete="off"
                inputMode="decimal"
                value={occupancySqFt}
                onChange={(e) => setOccupancySqFt(e.target.value)}
              />
              {errors.occupancySqFt && <p className="tosp-field-error">{errors.occupancySqFt}</p>}
            </div>
          </div>

          <div className="tosp-form-row">
            <div className="tosp-field">
              <label className="tosp-label" htmlFor="tosp-unit-bmva-sqft">
                BMVA SqFt <span className="tosp-required">*</span>
              </label>
              <input
                id="tosp-unit-bmva-sqft"
                className="manage-input"
                autoComplete="off"
                inputMode="decimal"
                value={bmvaSqFt}
                onChange={(e) => setBmvaSqFt(e.target.value)}
              />
              {errors.bmvaSqFt && <p className="tosp-field-error">{errors.bmvaSqFt}</p>}
            </div>

            {/* BMVA sets one rate for the whole village and revises it for
                everybody at once, so it's stated once under Configuration →
                BMVA Fees. Shown here because the fee below is meaningless
                without it. */}
            <div className="tosp-field">
              <span className="tosp-label">BMVA Rate per SqFt</span>
              <div
                className="tosp-calc-value"
                title="Set under Configuration → BMVA Fees, for every unit at once"
              >
                {formatCurrency(rate)}
              </div>
              <p className="tosp-field-hint">
                {exempt
                  ? byName
                    ? "This unit is exempt from the basic fee — set under Configuration → BMVA Fees."
                    : `${spaceType} is exempt from the basic fee.`
                  : configuration.bmvaRatePerSqFt === null
                    ? "This unit's own rate — set BMVA Fees under Configuration to use one rate everywhere."
                    : "From Configuration → BMVA Fees."}
              </p>
            </div>
          </div>

          {/* Calculated from BMVA SqFt × Rate — read-only by design, so a fee
              can never contradict the figures it comes from. */}
          <div className="tosp-form-row">
            <div className="tosp-field">
              <span className="tosp-label">BMVA Annual Fee</span>
              <div className="tosp-calc-value">{formatCurrency(annualFee)}</div>
            </div>
            <div className="tosp-field">
              <span className="tosp-label">BMVA Monthly Fee</span>
              <div className="tosp-calc-value">{formatCurrency(monthlyFee)}</div>
            </div>
          </div>

          {/* Tax rolls. A unit can hold many — C1 is ten — and they're never
              typed: they come off the municipality's bill on the Rolls tab and
              are picked from what's still unassigned. */}
          <div className="tosp-section">
            <div className="tosp-section-header">
              <span className="tosp-section-title">Tax Rolls</span>
            </div>

            {attached.length > 0 && (
              <div className="tosp-roll-list">
                {attached.map((ref) => {
                  const key = refKey(ref.roll, ref.part);
                  return (
                    <div key={key} className="tosp-roll-chip">
                      <div className="tosp-roll-chip-main">
                        <span className="tosp-roll-chip-number">{refLabel(ref)}</span>
                        <span className="tosp-roll-chip-detail">
                          {[
                            ref.roll.planNumber && `Plan ${ref.roll.planNumber}`,
                            ref.roll.level && `Level ${ref.roll.level}`,
                            ref.roll.unit && `Unit ${ref.roll.unit}`,
                          ]
                            .filter(Boolean)
                            .join(" · ") || ref.roll.legalDescription}
                          {ref.part.label && " · share by floor area"}
                        </span>
                      </div>
                      <button
                        type="button"
                        className="tosp-roll-chip-remove"
                        aria-label={`Remove ${refLabel(ref)}`}
                        title="Remove — returns this to the unassigned pool"
                        onClick={() =>
                          setTaxRollPartKeys((prev) => prev.filter((k) => k !== key))
                        }
                      >
                        ×
                      </button>
                    </div>
                  );
                })}
              </div>
            )}

            {picking ? (
              <div className="tosp-roll-picker">
                <div className="tosp-roll-picker-list">
                  {available.length === 0 ? (
                    <p className="tosp-field-hint" style={{ margin: 8 }}>
                      No unassigned rolls left.
                    </p>
                  ) : (
                    available.map((ref) => {
                      const key = refKey(ref.roll, ref.part);
                      return (
                        <label key={key} className="tosp-roll-picker-option">
                          <input
                            type="checkbox"
                            checked={checked.includes(key)}
                            onChange={(e) =>
                              setChecked((prev) =>
                                e.target.checked
                                  ? [...prev, key]
                                  : prev.filter((k) => k !== key),
                              )
                            }
                          />
                          <span className="tosp-roll-picker-roll">{refLabel(ref)}</span>
                          <span>{ref.roll.planNumber || "—"}</span>
                          <span>{ref.roll.level || "—"}</span>
                          <span>{ref.roll.unit || "—"}</span>
                        </label>
                      );
                    })
                  )}
                </div>

                <div className="tosp-roll-picker-footer">
                  <button type="button" className="btn-cancel" onClick={closePicker}>
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="tosp-btn-blue"
                    disabled={checked.length === 0}
                    onClick={addChecked}
                  >
                    {checked.length === 0
                      ? "Add selected"
                      : `Add ${checked.length} selected`}
                  </button>
                </div>
              </div>
            ) : (
              <>
                <button
                  type="button"
                  className="tosp-btn-blue"
                  disabled={available.length === 0}
                  onClick={() => setPicking(true)}
                >
                  Add Tax
                </button>
                {taxRolls.length === 0 ? (
                  <p className="tosp-field-hint">
                    No tax rolls imported yet — import the municipality's tax bill on the
                    Rolls tab first.
                  </p>
                ) : (
                  available.length === 0 && (
                    <p className="tosp-field-hint">
                      Every imported roll is already assigned to a unit.
                    </p>
                  )
                )}
              </>
            )}
          </div>

          <div style={{ display: "flex", justifyContent: "space-between" }}>
            {/* Cancel is held too: the writes are already in flight, and
                closing mid-save would hide a half-applied change. */}
            <button type="button" className="btn-cancel" disabled={busy} onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn-save" disabled={busy}>
              {busy ? (
                <>
                  <span className="tosp-spinner" aria-hidden="true" />
                  Saving…
                </>
              ) : (
                "Save"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
